// web/src/lib/sanitizeBidi.ts
//
// Strip Unicode bidi-override characters from user-supplied strings.
//
// Without this, a hostile-but-harmless-looking string like `boss‮sruoy`
// (using U+202E "RIGHT-TO-LEFT OVERRIDE") would render as `boss your` in
// an RTL/mixed context — flipping the semantic meaning. The same trick
// has been used in compiler payloads ("Trojan Source") and chat clients
// to spoof author identity in RTL columns.
//
// Scope is conservative: we strip the override + embedding control
// characters but leave `U+200E LEFT-TO-RIGHT MARK` and
// `U+200F RIGHT-TO-LEFT MARK` alone because those are semantically
// meaningful in legitimately-multilingual text and don't override
// surrounding direction.
//
// Codepoints removed:
//   U+202A LRE   LEFT-TO-RIGHT EMBEDDING
//   U+202B RLE   RIGHT-TO-LEFT EMBEDDING
//   U+202C PDF   POP DIRECTIONAL FORMATTING
//   U+202D LRO   LEFT-TO-RIGHT OVERRIDE
//   U+202E RLO   RIGHT-TO-LEFT OVERRIDE
//   U+2066 LRI   LEFT-TO-RIGHT ISOLATE
//   U+2067 RLI   RIGHT-TO-LEFT ISOLATE
//   U+2068 FSI   FIRST STRONG ISOLATE
//   U+2069 PDI   POP DIRECTIONAL ISOLATE

// Construct a fresh regex on each call site that needs match-state so
// the `g` flag's mutable `lastIndex` doesn't bite us; only `replace`
// resets it implicitly.
const BIDI_OVERRIDE_REPLACE = /[‪-‮⁦-⁩]/g;
const BIDI_OVERRIDE_PROBE = /[‪-‮⁦-⁩]/;

/**
 * Returns `value` with all Unicode bidi-override characters removed.
 * Idempotent: `sanitizeBidi(sanitizeBidi(x)) === sanitizeBidi(x)`.
 */
export function sanitizeBidi(value: string): string {
  return value.replace(BIDI_OVERRIDE_REPLACE, "");
}

/**
 * `true` iff `value` contains any bidi-override codepoint. Useful for
 * defensive logging on the few code paths that want to know the input
 * was rewritten.
 */
export function hasBidiOverride(value: string): boolean {
  return BIDI_OVERRIDE_PROBE.test(value);
}
